import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Dialog } from '@/components/ui/dialog';
import { Eye, FileText, Image, Download, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useGetAcceptanceRequests } from '@/hooks/useGiftsRequests';
import LoadingCircle from '@/components/LoadingCircle';
import { ICertificate } from '@/api/gifts-requests';
import { formatDate } from '@/lib/utils';
import Certificate from './certificate';
import { useProfileContext } from '@/hooks/useProfileContext';

const ViewCertificate = ({
  giftRequestId,
  assignedToId,
}: {
  giftRequestId: string;
  assignedToId: string;
}) => {
  const { profile } = useProfileContext();
  const [open, setOpen] = useState(false);
  const { data: certificatesData, isLoading } =
    useGetAcceptanceRequests(giftRequestId);

  // Filter certificates by gift request ID if provided
  const certificates = certificatesData?.items || [];

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <Image className="w-6 h-6 text-blue-500" />;
      default:
        return <FileText className="w-6 h-6 text-gray-500" />;
    }
  };

  const getFileType = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return extension?.toUpperCase() || 'FILE';
  };

  const isImageFile = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png'].includes(extension || '');
  };

  const isPdfFile = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf';
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderFilePreview = (certificate: ICertificate) => {
    if (isImageFile(certificate.name)) {
      return (
        <div className="relative group">
          <img
            src={certificate.url}
            alt={certificate.name}
            className="w-full h-48 object-cover rounded-lg border"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg border">
            <div className="text-center">
              <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Không thể tải ảnh</p>
            </div>
          </div>
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => window.open(certificate.url, '_blank')}
              className="bg-white/90 hover:bg-white"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Xem
            </Button>
          </div>
        </div>
      );
    }

    if (isPdfFile(certificate.name)) {
      return (
        <div className="border rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
          <div className="text-center">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-3" />
            <p className="font-medium text-gray-900 mb-2">{certificate.name}</p>
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(certificate.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Xem PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleDownload(certificate.url, certificate.name)
                }
              >
                <Download className="w-4 h-4 mr-1" />
                Tải về
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="border rounded-lg p-6 bg-gray-50">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-500 mx-auto mb-3" />
          <p className="font-medium text-gray-900 mb-2">{certificate.name}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDownload(certificate.url, certificate.name)}
          >
            <Download className="w-4 h-4 mr-1" />
            Tải về
          </Button>
        </div>
      </div>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: {
        label: 'Đang xử lý',
        className: 'bg-yellow-100 text-yellow-800',
      },
      done: { label: 'Hoàn thành', className: 'bg-green-100 text-green-800' },
      failed: { label: 'Thất bại', className: 'bg-red-100 text-red-800' },
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || {
      label: status,
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}
      >
        {statusInfo.label}
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="luxury-button-outline"
          onClick={() => setOpen(true)}
        >
          <Eye className="w-4 h-4" />
          Xem chứng từ
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[900px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">Xem chứng từ</DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">Danh sách chứng từ</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingCircle />
            <span className="ml-2">Đang tải chứng từ...</span>
          </div>
        ) : certificates.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Chưa có chứng từ nào</p>
            <p className="text-gray-400 text-sm">
              Chứng từ sẽ hiển thị ở đây sau khi được tải lên
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* File Statistics */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {certificates.length}
                </div>
                <div className="text-sm text-gray-600">Tổng số file</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {certificates.filter((c) => c.status === 'done').length}
                </div>
                <div className="text-sm text-gray-600">Đã hoàn thành</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {certificates.filter((c) => c.status === 'failed').length}
                </div>
                <div className="text-sm text-gray-600">Đã thất bại</div>
              </div>
            </div>

            {/* Files Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {certificates.map((certificate) => (
                <div
                  key={certificate.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  {/* File Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getFileIcon(certificate.name)}
                      <div>
                        <p
                          className="font-medium text-gray-900 truncate max-w-[200px]"
                          title={certificate.name}
                        >
                          {certificate.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getFileType(certificate.name)} •{' '}
                          {formatDate(certificate.createdAt)}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(certificate.status)}
                  </div>

                  {/* File Preview */}
                  {renderFilePreview(certificate)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-center pt-4 border-t gap-4">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="luxury-button-outline"
          >
            Đóng
          </Button>
          {profile?.id === assignedToId && <Certificate id={giftRequestId} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewCertificate;
